import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { fmt } from "@/components/prima_nota/utils";
import { FatturaEditor } from "@/components/prima_nota/FatturaEditor";

export default function PrimaNotaFatturePage() {
  const today = new Date();
  const yyyy = today.getFullYear();
  const isoToday = today.toISOString().slice(0, 10);
  const isoStart = `${yyyy}-01-01`;
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataDal, setDataDal] = useState(isoStart);
  const [dataAl, setDataAl] = useState(isoToday);
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [totaleImporto, setTotaleImporto] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tipoFilter, setTipoFilter] = useState("all"); // 'all' | 1 | 2 | 3 | 'delta'
  // 🆕 Feb 2026: default ordinamento = DATA discendente (fatture più recenti in cima)
  const [sortBy, setSortBy] = useState("data"); // null | 'prot' | 'data' | 'numero' | 'fornitore' | 'importo'
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const sortIndicator = (col) => {
    if (sortBy !== col) return <span style={{ opacity: 0.35, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  const carica = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dataDal) params.data_dal = dataDal;
      if (dataAl) params.data_al = dataAl;
      if (search) params.search = search;
      const r = await api.get("/pn/fatture", { params });
      setList(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotaleImporto(r.data.totale_importo || 0);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  // Integrity check (badge in cima alla pagina)
  const [integrity, setIntegrity] = useState(null);
  const checkIntegrity = async () => {
    try {
      const r = await api.get("/pn/maintenance/integrity-check");
      setIntegrity(r.data);
    } catch { setIntegrity(null); }
  };
  useEffect(() => { checkIntegrity(); }, []);
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, []);
  // Apri automaticamente una fattura via query param ?open=<id_doc>
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      apriEdit(Number(openId));
      // Rimuovi il param dall'URL dopo l'apertura per evitare ri-apertura su refresh
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { carica(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, dataDal, dataAl]);

  const apriNuova = () => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const todayDmy = `${dd}/${mm}/${d.getFullYear()}`;
    setEditing({ _isNew: true, prot_anno: yyyy, prot_tipo_id: 1, data_fattura: "", data_registrazione: todayDmy, numero_fattura: "", id_cliente: null, id_pagamento: null, totale_iva_compresa: 0, segno: "+" });
  };
  const apriEdit = async (id_doc) => {
    try { const r = await api.get(`/pn/fatture/${id_doc}`); setEditing(r.data); } catch { /* noop */ }
  };

  const toggleSegno = async (f, e) => {
    e.stopPropagation();
    if (Number(f.prot_tipo_id) !== 3) return;
    const nuovo = f.segno === "-" ? "+" : "-";
    try {
      await api.patch(`/pn/fatture/${f.id_doc}/segno`, null, { params: { segno: nuovo } });
      // ottimistico: aggiorna la lista locale senza refetch
      setList(prev => prev.map(x => x.id_doc === f.id_doc ? { ...x, segno: nuovo } : x));
    } catch (err) { alert(err.response?.data?.detail || "Errore"); }
  };

  // Counts per tab + totali con segno applicato (sulla lista filtrata da date/ricerca)
  const counts = { all: list.length, 1: 0, 2: 0, 3: 0, delta: 0 };
  let totFatNc = 0;     // Fatture(1) + Note Credito(2) con segno
  let totExtra = 0;     // Operazioni Extra(3) con segno
  for (const f of list) {
    const t = Number(f.prot_tipo_id);
    if (counts[t] !== undefined) counts[t]++;
    if (Math.abs(Number(f.delta || 0)) >= 0.01) counts.delta++;
    const imp = Number(f.totale_iva_compresa || 0);
    const seg = f.segno === "-" ? -1 : 1;
    if (t === 1 || t === 2) totFatNc += imp * seg;
    else if (t === 3) totExtra += imp * seg;
  }
  // Totali della selezione visibile (in base al tab)
  let totDisplayed = 0;
  for (const f of (tipoFilter === "all"
    ? list
    : tipoFilter === "delta"
      ? list.filter(f => Math.abs(Number(f.delta || 0)) >= 0.01)
      : list.filter(f => Number(f.prot_tipo_id) === tipoFilter))) {
    const seg = f.segno === "-" ? -1 : 1;
    totDisplayed += Number(f.totale_iva_compresa || 0) * seg;
  }
  const tabs = [
    { k: "all",   l: "Tutti",            color: "#37474f" },
    { k: 1,       l: "Fatture",          color: "#1565c0" },
    { k: 2,       l: "Note Credito",     color: "#ef6c00" },
    { k: 3,       l: "Operazioni Extra", color: "#6a1b9a" },
    { k: "delta", l: "Δ ≠ 0",            color: "#c62828" },
  ];

  const displayedRaw = tipoFilter === "all"
    ? list
    : tipoFilter === "delta"
      ? list.filter(f => Math.abs(Number(f.delta || 0)) >= 0.01)
      : list.filter(f => Number(f.prot_tipo_id) === tipoFilter);

  // Helper per ordinare data dd/mm/yyyy
  const dmyKey = (s) => {
    if (!s) return "";
    const m = String(s).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : String(s);
  };

  const displayed = (() => {
    if (!sortBy) return displayedRaw;
    const arr = [...displayedRaw];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case "prot":
          av = `${a.prot_anno}/${String(a.prot_numero || 0).padStart(8, "0")}`;
          bv = `${b.prot_anno}/${String(b.prot_numero || 0).padStart(8, "0")}`;
          break;
        case "data":
          av = dmyKey(a.data_fattura);
          bv = dmyKey(b.data_fattura);
          break;
        case "numero":
          av = (a.numero_fattura || "").toLowerCase();
          bv = (b.numero_fattura || "").toLowerCase();
          break;
        case "fornitore":
          av = (a.fornitore || a.descrizione_extra || "").toLowerCase();
          bv = (b.fornitore || b.descrizione_extra || "").toLowerCase();
          break;
        case "importo": {
          // 🆕 Feb 2026: ordina per IMPORTO totale (numerico)
          const va = Number(a.totale_iva_compresa || 0) * (a.segno === "-" || a.prot_tipo_id === 2 ? -1 : 1);
          const vb = Number(b.totale_iva_compresa || 0) * (b.segno === "-" || b.prot_tipo_id === 2 ? -1 : 1);
          av = va; bv = vb;
          break;
        }
        default:
          av = ""; bv = "";
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  })();

  return (
    <div>
      <div className="sw-page-title">
        <h1>📒 Prima Nota — Lista Fatture</h1>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {integrity && (
            (() => {
              const ok = integrity.status === "ok";
              const dg = integrity.duplicates_groups || 0;
              const rdg = integrity.righe_duplicates_groups || 0;
              const rde = integrity.righe_duplicates_excess || 0;
              const de = integrity.duplicates_excess || 0;
              const orph = integrity.orphan_righe || 0;
              const nullIds = integrity.null_id_doc || 0;
              const parts = [];
              if (dg) parts.push(`${dg} fatture dup. (${de})`);
              if (rdg) parts.push(`${rdg} righe dup. (${rde})`);
              if (orph) parts.push(`${orph} righe orfane`);
              if (nullIds) parts.push(`${nullIds} id_doc null`);
              const label = ok ? "✓ Integrità OK" : `⚠ ${parts.join(" · ")}`;
              const tooltip = ok
                ? "Nessun duplicato, nessuna riga orfana, nessun id_doc mancante."
                : `Fatture duplicate: ${dg} gruppi (${de} record in eccesso)\nRighe duplicate: ${rdg} gruppi (${rde} record in eccesso)\nRighe orfane: ${orph}\nRecord con id_doc null: ${nullIds}\n\nAccedi alla gestione Admin → Impostazioni → Manutenzione Prima Nota per pulire.`;
              return (
                <span data-testid="pn-integrity-badge" title={tooltip}
                  style={{
                    padding: "6px 12px",
                    background: ok ? "#e8f5e9" : "#ffebee",
                    color: ok ? "#1b5e20" : "#c62828",
                    border: `1px solid ${ok ? "#2e7d32" : "#c62828"}`,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    cursor: "help",
                  }}>{label}</span>
              );
            })()
          )}
          <button onClick={apriNuova} data-testid="pn-fat-new"
            style={{
              padding: "10px 22px",
              background: "linear-gradient(180deg, #d32f2f 0%, #b71c1c 100%)",
              color: "white",
              border: "2px solid #7f0000",
              fontSize: 15,
              fontWeight: 800,
              letterSpacing: 0.5,
              cursor: "pointer",
              boxShadow: "0 3px 10px rgba(183,28,28,0.45)",
              textTransform: "uppercase",
            }}>
            <span style={{ color: "white", fontWeight: 900, marginRight: 4 }}>+</span> Nuova Operazione
          </button>
        </div>
      </div>

      <div style={{ margin: "8px 20px", padding: 10, background: "white", border: "1px solid #ccc", display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Da data</div>
          <input type="date" value={dataDal} onChange={e => setDataDal(e.target.value)} data-testid="pn-fat-data-dal"
            style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>A data</div>
          <input type="date" value={dataAl} onChange={e => setDataAl(e.target.value)} data-testid="pn-fat-data-al"
            style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div style={{ flex: "1 1 250px" }}>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Cerca fornitore / n° fattura / protocollo</div>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} data-testid="pn-fat-search"
            placeholder="es. ENEL, A2A, 526503, 12..."
            style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <button onClick={carica} disabled={loading} style={{ padding: "6px 16px", background: "#00695c", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{loading ? "..." : "↻ Aggiorna"}</button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "#444", display: "flex", gap: 14, alignItems: "stretch" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center", borderRight: "1px solid #ddd", paddingRight: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#666", letterSpacing: 0.3 }}>FATTURE − N.CREDITO</div>
            <div data-testid="pn-fat-tot-fatnc" style={{ fontFamily: "monospace", fontSize: 14, color: "#1565c0", fontWeight: 800 }}>€ {Number(totFatNc).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center", borderRight: "1px solid #ddd", paddingRight: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#666", letterSpacing: 0.3 }}>OP. EXTRA (con segno)</div>
            <div data-testid="pn-fat-tot-extra" style={{ fontFamily: "monospace", fontSize: 14, color: "#6a1b9a", fontWeight: 800 }}>€ {Number(totExtra).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#666", letterSpacing: 0.3 }}>{tipoFilter === "all" ? "TOTALE LISTA" : "SELEZIONE"} ({tipoFilter === "all" ? total : (tipoFilter === "delta" ? counts.delta : counts[tipoFilter])} righe)</div>
            <div data-testid="pn-fat-tot-displayed" style={{ fontFamily: "monospace", fontSize: 15, color: "#c62828", fontWeight: 800 }}>€ {Number(totDisplayed).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      </div>

      <div style={{ margin: "0 20px", display: "flex", gap: 0, borderBottom: "2px solid #37474f" }}>
        {tabs.map(t => {
          const active = tipoFilter === t.k;
          const n = counts[t.k] ?? 0;
          return (
            <button key={t.k} onClick={() => setTipoFilter(t.k)} data-testid={`pn-fat-tab-${t.k}`}
              style={{
                padding: "10px 22px",
                background: active ? t.color : "#eee",
                color: active ? "white" : "#666",
                border: "none",
                borderTop: active ? `3px solid ${t.color}` : "3px solid #ccc",
                borderLeft: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                fontSize: 14, fontWeight: 700, cursor: "pointer",
              }}>
              {t.l} <span style={{ marginLeft: 8, padding: "2px 9px", background: active ? "rgba(255,255,255,0.3)" : "#ccc", color: active ? "white" : "#444", borderRadius: 8, fontSize: 11, fontWeight: 800 }}>{n}</span>
            </button>
          );
        })}
      </div>

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", borderTop: 0, maxHeight: "70vh", overflow: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
          <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white", zIndex: 1 }}>
            <tr>
              <th style={{ width: 32 }}>#</th>
              <th style={{ width: 110 }}>CAUSALE</th>
              <th onClick={() => toggleSort("prot")} data-testid="pn-fat-sort-prot"
                style={{ width: 80, cursor: "pointer", userSelect: "none" }} title="Ordina per protocollo">
                PROT.{sortIndicator("prot")}
              </th>
              <th onClick={() => toggleSort("data")} data-testid="pn-fat-sort-data"
                style={{ width: 100, cursor: "pointer", userSelect: "none" }} title="Ordina per data fattura">
                DATA{sortIndicator("data")}
              </th>
              <th onClick={() => toggleSort("numero")} data-testid="pn-fat-sort-numero"
                style={{ width: 130, cursor: "pointer", userSelect: "none" }} title="Ordina per N° fattura">
                N° FATT.{sortIndicator("numero")}
              </th>
              <th onClick={() => toggleSort("fornitore")} data-testid="pn-fat-sort-fornitore"
                style={{ minWidth: 220, cursor: "pointer", userSelect: "none" }} title="Ordina per fornitore / descrizione">
                FORNITORE / DESCRIZIONE{sortIndicator("fornitore")}
              </th>
              {tipoFilter === 3 && <th style={{ minWidth: 220, background: "#f3e5f5" }} title="Conti delle righe contabili collegate">CONTI</th>}
              <th style={{ width: 90, textAlign: "center" }}>C/R</th>
              <th onClick={() => toggleSort("importo")} data-testid="pn-fat-sort-importo"
                style={{ width: 120, textAlign: "right", cursor: "pointer", userSelect: "none" }} title="Ordina per importo">
                IMPORTO{sortIndicator("importo")}
              </th>
              <th style={{ width: 110, textAlign: "right" }}>Σ COSTI</th>
              <th style={{ width: 110, textAlign: "right" }}>Δ DELTA</th>
              <th style={{ width: 90, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody key={`tbody-${tipoFilter}`}>
            {displayed.length === 0 && <tr><td colSpan={tipoFilter === 3 ? 12 : 11} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessuna fattura.</td></tr>}
            {displayed.map((f, i) => {
              const isExtra = f.prot_tipo_id === 3;
              const isNotaCredito = f.prot_tipo_id === 2;
              const delta = Number(f.delta || 0);
              const isQuadrato = Math.abs(delta) < 0.01;
              return (
                <tr key={f.id_doc} data-testid={`pn-fat-row-${i}`}>
                  <td style={{ textAlign: "center", color: "#888", fontFamily: "monospace" }}>{i+1}</td>
                  <td>
                    <span style={{
                      padding: "2px 6px", fontSize: 10, fontWeight: 700, color: "white",
                      background: isExtra ? "#6a1b9a" : (isNotaCredito ? "#ef6c00" : "#1565c0"),
                      fontFamily: "monospace"
                    }}>
                      {isExtra ? "EXTRA" : (isNotaCredito ? "N.CRED." : "FATTURA")}
                    </span>
                  </td>
                  <td style={{ fontFamily: "monospace", fontWeight: 700, color: "#0d47a1" }}>{f.prot_anno}/{f.prot_numero}</td>
                  <td style={{ fontFamily: "monospace" }}>{f.data_fattura || "—"}</td>
                  <td style={{ fontFamily: "monospace" }}>{f.numero_fattura || "—"}</td>
                  <td style={{ fontWeight: 600, textTransform: "uppercase" }}>
                    {isExtra
                      ? (f.descrizione_extra || <span style={{ color: "#bbb" }}>—</span>)
                      : (f.fornitore || f.descrizione_extra || <span style={{ color: "#bbb" }}>{f.id_cliente || "—"}</span>)}
                  </td>
                  {tipoFilter === 3 && (
                    <td style={{ background: "#fafafa", fontSize: 11, lineHeight: 1.3, padding: "4px 8px" }}>
                      {(f.conti_righe || []).length === 0 ? (
                        <span style={{ color: "#bbb", fontStyle: "italic" }}>— nessuna riga —</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {(f.conti_righe || []).map((c, j) => (
                            <div key={j} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                              <span style={{ color: "#37474f" }}>
                                <span style={{ fontFamily: "monospace", color: "#0d47a1", fontWeight: 700 }}>{c.id_conto}</span>
                                {" "}
                                <span>{c.descrizione || "(senza descrizione)"}</span>
                              </span>
                              <span style={{ fontFamily: "monospace", fontWeight: 700, color: c.tot < 0 ? "#c62828" : "#1b5e20", whiteSpace: "nowrap" }}>
                                € {fmt(c.tot)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  )}
                  <td style={{ textAlign: "center", padding: 0 }}>
                    {(() => {
                      // Mappa segno → etichetta semantica
                      // tipo 1 (Fattura) → "+ COSTO" verde
                      // tipo 2 (N.Cred.) → "- COSTO" rosso
                      // tipo 3 (Extra) → "+ RICAVO" se segno +, "+ COSTO" se segno -
                      let label, bg;
                      if (isExtra) {
                        if (f.segno === "-") { label = "+ COSTO"; bg = "#ef6c00"; }
                        else { label = "+ RICAVO"; bg = "#1b5e20"; }
                      } else if (isNotaCredito) {
                        label = "- COSTO"; bg = "#c62828";
                      } else {
                        label = "+ COSTO"; bg = "#1b5e20";
                      }
                      const labelStyle = {
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: "100%", padding: "6px 0",
                        background: bg, color: "white", border: "none",
                        fontFamily: "Inter, system-ui, sans-serif",
                        fontWeight: 800, fontSize: 11, letterSpacing: 0.3,
                        lineHeight: 1, textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                      };
                      if (isExtra) {
                        return (
                          <button onClick={(e) => toggleSegno(f, e)} data-testid={`pn-fat-toggle-segno-${i}`}
                            title="Clicca per cambiare tra COSTO e RICAVO"
                            style={{ ...labelStyle, cursor: "pointer" }}>{label}</button>
                        );
                      }
                      return <span style={labelStyle}>{label}</span>;
                    })()}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700 }}>€ {fmt(f.totale_iva_compresa)}</td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", color: "#555" }}>{fmt(f.totale_righe || 0)}</td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: isQuadrato ? "#1b5e20" : "#c62828", background: isQuadrato ? "#e8f5e9" : "#ffebee" }}>
                    {isQuadrato ? "✓" : fmt(delta)}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button onClick={() => apriEdit(f.id_doc)} data-testid={`pn-fat-edit-${i}`}
                      style={{ padding: "3px 9px", background: "#1565c0", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>✏ Apri</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <FatturaEditor
        f={editing}
        onClose={() => { setEditing(null); carica(); }}
        onDeleted={(deletedId) => {
          // Update ottimistico: rimuovi subito la riga dalla lista locale (no flicker)
          setList(prev => prev.filter(x => x.id_doc !== deletedId));
          setTotal(t => Math.max(0, (t || 0) - 1));
        }}
      />}
    </div>
  );
}
